import { memo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useRegisterSW } from "virtual:pwa-register/react";
import { debugValue } from "@/env/utils";
import { Dialog } from "@/lib/capacitor/dialog";
import { useMe } from "@/lib/hooks/use-me";
import { AppLifecycleProvider } from "@/lib/providers/app-lifecycle";
import { OrganizationAutoSwitchProvider } from "@/lib/providers/organization-auto-switch";
import { PermissionsProvider } from "@/lib/providers/permission";
import { PostHogProvider } from "@/lib/providers/posthog/components/Provider";
import { ConfigProvider } from "@/lib/providers/posthog/config";
import { QueryProvider } from "@/lib/providers/query/provider";
import { SessionProvider } from "@/lib/providers/session";
import { ThemeProvider } from "@/lib/providers/theme/provider";
import { WebSocketProvider } from "@/lib/providers/websocket";
import { StoreProvider } from "@/lib/store/provider";
import { Compose } from "@/utils/components/compose";

export function Providers(props: React.PropsWithChildren) {
  const { t } = useTranslation();

  const { updateServiceWorker } = useRegisterSW({
    immediate: true,
    onNeedRefresh,
  });

  function onNeedRefresh() {
    Dialog.confirm({
      title: t("pwa.on-need-refresh.title"),
      message: t("pwa.on-need-refresh.message"),
      okButtonTitle: t("pwa.on-need-refresh.ok"),
      cancelButtonTitle: t("pwa.on-need-refresh.cancel"),
    }).then((result) => {
      if (!result) {
        return;
      }
      updateServiceWorker();
    });
  }

  return (
    <Compose
      providers={[
        // CRITICAL: Provider order matters for initialization!
        // DO NOT reorder without understanding dependencies below:

        // 1. QueryProvider - Must be first (React Query setup)
        <QueryProvider key="react-query" />,

        // 2. ThemeProvider - Can initialize early
        <ThemeProvider key="theme" />,

        // 3. SessionProvider - Creates auth state and queries
        // MUST come before AppLifecycleProvider and WebSocketProvider
        <SessionProvider key="session" />,

        // 4. AppLifecycleProvider - Refetches queries on resume
        // DEPENDS ON: SessionProvider creating ["me"] query first
        <AppLifecycleProvider key="app-lifecycle" />,

        // 5. StoreProvider - Zustand state management
        <StoreProvider key="zustand" />,

        // 6. WebSocketProvider - Real-time updates
        // DEPENDS ON: SessionProvider for auth state
        <WebSocketProvider key="websocket" />,

        // 7. PostHogProvider - Analytics
        <PostHogProvider key="posthog" />,

        // 8. ConfigProvider - App configuration
        <ConfigProvider key="config" />,

        // 9. DevelopmentProvider - Dev tools
        <DevelopmentProvider key="development" />,

        // 10. DataProvider - Fetches initial data
        // DEPENDS ON: SessionProvider for auth state
        <DataProvider key="data" />,

        // 11. OrganizationAutoSwitchProvider - Currently disabled
        // <OrganizationAutoSwitchProvider key="organization-auto-switch" />,

        // 12. PermissionsProvider - Checks permissions
        <PermissionsProvider key="permissions" />,
      ]}
      {...props}
    />
  );
}

function DevelopmentProvider(props: React.PropsWithChildren) {
  const className = "debug_ui_touch-target";

  useEffect(() => {
    if (debugValue("ui:touch-target")) {
      document.documentElement.classList.add(className);
    } else {
      document.documentElement.classList.remove(className);
    }

    return () => {
      document.documentElement.classList.remove(className);
    };
  }, []);

  return <>{props.children}</>;
}

const DataProvider = memo(function DataProvider(props: React.PropsWithChildren) {
  useMe();

  return <>{props.children}</>;
});
